import {
  Grid,
  makeStyles,
  Tooltip,
  Typography,
  TypographyProps,
} from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import { CSSProperties, ReactNode } from 'react';
import { WbFieldLabel } from '../WbFieldLabel';
import { WbIcon, WbIconDefinition } from '../WbIcon';
import { LinkWrapper } from './LinkWrapper';
import { WbShowMoreElement } from '../WbShowMoreElement';
import clsx from 'clsx';

type StylesProps =
  | { iconVerticalAlignment?: 'start' | 'center' | 'end' }
  | undefined;

const useStyle = makeStyles(theme => ({
  text: {
    overflow: 'hidden',
    fontSize: theme.typography.fontSize,
    wordBreak: 'break-word',
  },
  label: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    lineHeight: '14px',
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  wordBreak: {
    paddingTop: 2,
    wordBreak: 'break-word',
    display: 'flex',
    alignItems: 'start',
  },
  textWithIcon: (props: StylesProps = { iconVerticalAlignment: 'start' }) => ({
    display: 'flex !important',
    alignItems: props?.iconVerticalAlignment,
  }),
  span: {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

type Props = {
  label?: string;
  value?: ReactNode;
  /**
   * supported types are:
   * 'string' | 'date'
   */
  type?: string;
  href?: string;
  hrefTarget?: string;
  textVariant?: 'body1' | 'body2';
  showTooltip?: boolean;
  icon?: WbIconDefinition;
  maxLines?: number;
  gridItem?: boolean;
  gridSizes?: Record<string, number>;
  contentProps?: TypographyProps;
  labelStyle?: CSSProperties;
  showMoreButton?: boolean;
  dateFormat?: string;
  iconAlignment?: 'left' | 'right';
  iconVerticalAlignment?: 'start' | 'center' | 'end';
};

const IconWrapper = ({
  icon,
  contentProps,
  textVariant,
  children,
  iconAlignment,
  iconVerticalAlignment = 'start',
}: {
  icon?: WbIconDefinition;
  contentProps?: TypographyProps;
  textVariant?: 'body1' | 'body2';
  children?: ReactNode;
  iconAlignment?: 'left' | 'right';
  iconVerticalAlignment?: 'start' | 'center' | 'end';
}) => {
  const classes = useStyle({ iconVerticalAlignment });

  const content = (
    <Typography
      variant={textVariant}
      className={clsx(classes.wordBreak, icon && classes.textWithIcon)}
      style={icon ? { alignItems: iconVerticalAlignment } : {}}
      {...contentProps}
    >
      {!!icon && (!iconAlignment || iconAlignment === 'left') && (
        <WbIcon icon={icon} style={{ marginRight: 4 }} />
      )}
      {children}
      {!!icon && iconAlignment === 'right' && (
        <WbIcon icon={icon} style={{ marginLeft: 4 }} />
      )}
    </Typography>
  );

  return content;
};

const Content = ({
  value,
  type,
  href,
  hrefTarget,
  maxLines,
  icon,
  textVariant,
  contentProps,
  dateFormat = 'yyyy/MM/dd HH:mm:ss',
  showMoreButton = false,
  showTooltip,
  iconAlignment,
  iconVerticalAlignment,
}: Omit<Props, 'label'>) => {
  let displayValue: string;

  const classes = useStyle();

  const iconWrapperProps = {
    icon,
    contentProps,
    textVariant,
    iconAlignment,
    iconVerticalAlignment,
  };

  if (typeof value === 'string') {
    displayValue = value;
    if (type === 'date') {
      try {
        displayValue = format(parseISO(value as string), dateFormat);
      } catch (e) {
        displayValue = value;
      }
    }
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    displayValue = value.toString();
  } else if (!value && !!icon) {
    return <IconWrapper {...iconWrapperProps} />;
  } else if (!value) {
    displayValue = '-';
  } else {
    // this is the case when value is a ReactNode
    return <>{value}</>;
  }

  if (href)
    return (
      <IconWrapper {...iconWrapperProps}>
        <LinkWrapper
          value={displayValue}
          href={href}
          hrefTarget={hrefTarget}
          showTooltip
        />
      </IconWrapper>
    );

  if ((maxLines === undefined || maxLines === -1) && !showMoreButton)
    return (
      <IconWrapper {...iconWrapperProps}>
        {showTooltip ? (
          <Tooltip title={displayValue}>
            <span className={classes.span}>{displayValue}</span>
          </Tooltip>
        ) : (
          displayValue
        )}
      </IconWrapper>
    );

  // in this case, tooltip is handled by WbShowMoreElement
  return (
    <IconWrapper {...iconWrapperProps}>
      <span>
        <WbShowMoreElement maxLines={maxLines} showMoreButton={showMoreButton}>
          {displayValue}
        </WbShowMoreElement>
      </span>
    </IconWrapper>
  );
};

export const GenericField = ({
  label,
  gridSizes,
  gridItem = true,
  labelStyle,
  ...content
}: Props) => {
  const wrappedContent = (
    <>
      {label && <WbFieldLabel label={label} style={labelStyle} />}
      <Content {...content} />
    </>
  );

  if (gridItem) {
    return (
      <Grid item {...gridSizes}>
        {wrappedContent}
      </Grid>
    );
  }

  return wrappedContent;
};
